import { actionTypes } from '@0xtorch/core'
/**
 * portfolio アプリで利用する SQLite schema を定義する。
 * drizzle の table 定義を集約し、query 層と migration 層の基準となる型を提供する。
 */
import {
  taxTypes,
  transactionCategories,
  transactionSubCategories,
} from '@pkg/accounting'
import { relations, sql } from 'drizzle-orm'
import {
  blob,
  foreignKey,
  index,
  int,
  integer,
  sqliteTable,
  text,
  unique,
  uniqueIndex,
} from 'drizzle-orm/sqlite-core'

const actionTransferDirections = ['in', 'out', 'none'] as const
// core packageは型/schemaのみを公開するため、SQLite enum用tupleをDB境界で保持する。
const actionEvidences = [
  'contract',
  'system-rule',
  'user-rule',
  'manual-check',
  'assistant-check',
  'none',
] as const
// core packageは型/schemaのみを公開するため、SQLite enum用tupleをDB境界で保持する。
const actionCrossTypes = ['start', 'middle', 'end'] as const
const arrayConditionOperators = ['in-array', 'not-in-array'] as const
const mathConditionOperators = ['eq', 'gte', 'lte'] as const
const transferConditionOperators = [
  'in-array',
  'not-in-array',
  'in-array-from',
  'not-in-array-from',
  'in-array-to',
  'not-in-array-to',
] as const
const textConditionOperators = [
  'eq',
  'like',
  'not-like',
  'is-empty',
  'is-not-empty',
] as const
const priceTargetOperators = ['is-empty', 'is-not-empty'] as const

export const portfolioTable = sqliteTable('portfolio', {
  /** Unique ID */
  id: text('id').primaryKey(),
  /** Name of the portfolio */
  name: text('name').notNull(),
  /** Fiat currency id */
  fiat: text('fiat', { enum: ['usd', 'jpy'] }).notNull(),
  timezone: text('timezone').notNull(),
  /** Start month of accounting period */
  startMonth: int('startMonth').notNull(),
  /** Cost basis Method for profit/loss calculation
   * - FIFO: First In First Out
   * - LIFO: Last In First Out
   * - ACB: Average Cost Basis
   * - WACB: Weighted Average Cost Basis
   */
  costBasis: text('costBasis', {
    enum: ['FIFO', 'LIFO', 'ACB', 'WACB'],
  }).notNull(),
  handleLoanAsTrade: int('handleLoanAsTrade', {
    mode: 'boolean',
  }),
  handleReplaceAsTrade: int('handleReplaceAsTrade', {
    mode: 'boolean',
  }),
  use5PercentRule: int('use5PercentRule', {
    mode: 'boolean',
  }),
  /** Whether to need period end market value evaluation & profit/loss calculation */
  needPeriodEndEvaluation: int('needPeriodEndEvaluation', {
    mode: 'boolean',
  }).notNull(),
  closedAccountingPeriodId: int('closedAccountingPeriodId').references(
    () => accountingPeriodTable.id,
  ),
  isTxGenerated: int('isTxGenerated', {
    mode: 'boolean',
  }).notNull(),
  startTime: int('startTime', { mode: 'timestamp_ms' }),
})

export const accountTable = sqliteTable(
  'account',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    group: text('group').notNull(),
    name: text('name').notNull(),
    type: text('type', {
      enum: [
        'evm',
        'polkadot',
        'solana',
        'cardano',
        'xrpl',
        'exchange',
        'service',
      ],
    }).notNull(),
    category: text('category').notNull(),
    syncing: int('syncing', { mode: 'boolean' }),
    lastSyncedAt: int('lastSyncedAt', { mode: 'timestamp_ms' }),
    /** @deprecated */
    syncRegardlessOfUsage: int('syncRegardlessOfUsage', {
      mode: 'boolean',
    }),
    evmChainId: int('evmChainId'),
    evmAddress: blob('evmAddress').$type<Uint8Array>(),
    evmToBlock: int('evmToBlock'),
    evmLastTxCount: int('evmLastTxCount'),
    polkadotChainId: int('polkadotChainId'),
    polkadotAddress: text('polkadotAddress'),
    polkadotToBlock: int('polkadotToBlock'),
    solanaAddress: text('solanaAddress'),
    /** Mainnet の Classic Address。 */
    xrplAddress: text('xrplAddress'),
    /** 必要な ledger 範囲の取得完了。解析完了とは区別する。 */
    xrplIndexComplete: int('xrplIndexComplete', { mode: 'boolean' }),
    /** 登録時の入力アドレス。表示名の変更後も保持する。 */
    cardanoAddress: text('cardanoAddress'),
    /** mainnet の鍵由来 Stake Address。Enterprise では null。 */
    cardanoStakeAddress: text('cardanoStakeAddress'),
    /** ページ探索の完了と未解析イベントの有無を別々に管理する。 */
    cardanoIndexComplete: int('cardanoIndexComplete', { mode: 'boolean' }),
    exchangeApiKey: text('exchangeApiKey'),
    exchangeApiSecret: text('exchangeApiSecret'),
    exchangeApiPassword: text('exchangeApiPassword'),
    apiStartTime: int('apiStartTime', { mode: 'timestamp_ms' }),
  },
  (table) => [
    unique().on(table.evmChainId, table.evmAddress),
    unique().on(table.polkadotChainId, table.polkadotAddress),
    unique().on(table.solanaAddress),
    unique().on(table.xrplAddress),
    unique().on(table.cardanoStakeAddress),
    unique().on(table.cardanoAddress),
  ],
)

/** アカウント一覧の1行に対応するグループの手動表示順。未登録グループは作成順で末尾に表示する。 */
export const accountDisplayOrderTable = sqliteTable('accountDisplayOrder', {
  group: text('group').primaryKey(),
  displayOrder: int('displayOrder').notNull(),
})

export const accountCsvTable = sqliteTable(
  'accountCsv',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    csvId: text('csvId').notNull(),
    csvName: text('csvName').notNull(),
  },
  (table) => [index('unq_accountCsv_accountId').on(table.accountId)],
)

export const accountExchangeApiTable = sqliteTable(
  'accountExchangeApi',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    apiId: text('apiId').notNull(),
    from: int('from', { mode: 'timestamp_ms' }).notNull(),
    to: int('to', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [
    uniqueIndex('unq_accountExchangeApi').on(table.accountId, table.apiId),
  ],
)

export const accountApiTable = sqliteTable(
  'accountApi',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    subType: text('subType'),
    type: text('type').notNull(),
    from: int('from', { mode: 'timestamp_ms' }),
    to: int('to', { mode: 'timestamp_ms' }),
    stopped: int('stopped', { mode: 'boolean' }),
  },
  (table) => [
    uniqueIndex('unq_accountApi').on(
      table.accountId,
      table.type,
      table.subType,
    ),
  ],
)

export const accountApiLostTable = sqliteTable(
  'accountApiLost',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    apiId: int('apiId')
      .notNull()
      .references(() => accountApiTable.id, {
        onDelete: 'cascade',
      }),
    from: int('from', { mode: 'timestamp_ms' }).notNull(),
    to: int('to', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [index('unq_accountApiLost_apiId').on(table.apiId)],
)

export const accountApiPendingTable = sqliteTable(
  'accountApiPending',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    apiId: int('apiId')
      .notNull()
      .references(() => accountApiTable.id, {
        onDelete: 'cascade',
      }),
    from: int('from', { mode: 'timestamp_ms' }).notNull(),
    to: int('to', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [index('unq_accountApiPending_apiId').on(table.apiId)],
)

export const accountEvmCsvTable = sqliteTable(
  'accountEvmCsv',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    csvId: text('csvId').notNull(),
    order: int('order').notNull(),
    fileName: text('fileName').notNull(),
    hashCount: int('hashCount').notNull(),
    from: int('from', { mode: 'timestamp_ms' }).notNull(),
    to: int('to', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [
    uniqueIndex('unq_accountEvmCsv').on(
      table.accountId,
      table.csvId,
      table.order,
    ),
  ],
)

export const evmTxIndexTable = sqliteTable(
  'evmTxIndex',
  {
    chainId: int('chainId').notNull(),
    hash: blob('hash').$type<Uint8Array>().notNull(),
    blockNumber: int('blockNumber').notNull(),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
    input: blob('input').$type<Uint8Array>(),
    value: blob('value').$type<Uint8Array>(),
  },
  (table) => [uniqueIndex('idx_evmTxIndex').on(table.chainId, table.hash)],
)

export const accountEvmTxIndexRelationTable = sqliteTable(
  'accountEvmTxIndexRelation',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    hash: blob('hash').$type<Uint8Array>().notNull(),
    analyzed: int('analyzed', { mode: 'boolean' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_accountEvmTxIndexRelation').on(
      table.accountId,
      table.hash,
    ),
  ],
)

export const polkadotExtrinsicTable = sqliteTable(
  'polkadotExtrinsic',
  {
    chainId: int('chainId').notNull(),
    blockNumber: int('blockNumber').notNull(),
    extrinsicIndex: int('extrinsicIndex'),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_polkadotExtrinsic').on(
      table.chainId,
      table.blockNumber,
      table.extrinsicIndex,
    ),
  ],
)

export const accountPolkadotExtrinsicRelationTable = sqliteTable(
  'accountPolkadotExtrinsicRelation',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    blockNumber: int('blockNumber').notNull(),
    extrinsicIndex: int('extrinsicIndex'),
    analyzed: int('analyzed', { mode: 'boolean' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_accountPolkadotExtrinsicRelation').on(
      table.accountId,
      table.blockNumber,
      table.extrinsicIndex,
    ),
  ],
)

export const solanaSignatureTable = sqliteTable(
  'solanaSignature',
  {
    signature: text('signature').primaryKey(),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [index('idx_solanaSignature_timestamp').on(table.timestamp)],
)

export const accountSolanaSignatureTable = sqliteTable(
  'accountSolanaSignature',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    signature: text('signature').notNull(),
    analyzed: int('analyzed', { mode: 'boolean' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_accountSolanaSignature').on(
      table.accountId,
      table.signature,
    ),
  ],
)

export const solanaTokenAccountTable = sqliteTable(
  'solanaTokenAccount',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    address: text('address').notNull(),
  },
  (table) => [
    uniqueIndex('idx_solanaTokenAccount').on(table.accountId, table.address),
  ],
)

/** XRPL の取得済み生取引とアカウント関連を保持し、解析失敗時も再処理可能にする。 */
export const xrplTransactionIndexTable = sqliteTable(
  'xrplTransactionIndex',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, { onDelete: 'cascade' }),
    sourceId: text('sourceId').notNull(),
    hash: text('hash').notNull(),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
    ledgerIndex: int('ledgerIndex').notNull(),
    raw: text('raw').notNull(),
    analyzed: int('analyzed', { mode: 'boolean' }).notNull().default(false),
  },
  (table) => [
    uniqueIndex('idx_xrplTransactionIndex').on(table.accountId, table.sourceId),
    index('idx_xrplTransactionIndex_pending').on(
      table.analyzed,
      table.timestamp,
    ),
  ],
)

/** XRPL の固定検索範囲と次ページ位置。完了済み範囲は差分同期の境界に使う。 */
export const xrplSyncCheckpointTable = sqliteTable('xrplSyncCheckpoint', {
  accountId: int('accountId')
    .primaryKey()
    .references(() => accountTable.id, { onDelete: 'cascade' }),
  startTime: int('startTime').notNull(),
  ledgerMin: int('ledgerMin').notNull(),
  ledgerMax: int('ledgerMax').notNull(),
  marker: text('marker'),
  complete: int('complete', { mode: 'boolean' }).notNull(),
})

/** 自己保有と判定した通常の Cardano Key Payment Base Address。 */
export const cardanoPaymentAddressTable = sqliteTable(
  'cardanoPaymentAddress',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, { onDelete: 'cascade' }),
    address: text('address').notNull(),
  },
  (table) => [
    uniqueIndex('idx_cardanoPaymentAddress').on(table.accountId, table.address),
  ],
)

/** Cardano の各探索ストリームのページ保存位置。末尾ページは次回も再取得する。 */
export const cardanoSyncCheckpointTable = sqliteTable(
  'cardanoSyncCheckpoint',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, { onDelete: 'cascade' }),
    stream: text('stream').notNull(),
    page: int('page').notNull(),
    complete: int('complete', { mode: 'boolean' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_cardanoSyncCheckpoint').on(table.accountId, table.stream),
  ],
)

/** Tx と Tx Hash を持たない報酬配分を区別した、アカウント別の解析索引。 */
export const cardanoEventIndexTable = sqliteTable(
  'cardanoEventIndex',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, { onDelete: 'cascade' }),
    sourceId: text('sourceId').notNull(),
    kind: text('kind', { enum: ['tx', 'reward'] }).notNull(),
    txHash: text('txHash'),
    payoutEpoch: int('payoutEpoch'),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
    /** 報酬額は lovelace 整数文字列で保存し、Number へ変換しない。 */
    rewardAmount: text('rewardAmount'),
    analyzed: int('analyzed', { mode: 'boolean' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_cardanoEventIndex').on(table.accountId, table.sourceId),
    index('idx_cardanoEventIndex_pending').on(table.analyzed, table.timestamp),
  ],
)

export const evmInternalTransactionTable = sqliteTable(
  'evmInternalTransaction',
  {
    chainId: int('chainId').notNull(),
    contractAddress: blob('contractAddress').$type<Uint8Array>().notNull(),
    from: blob('from').$type<Uint8Array>().notNull(),
    gas: blob('gas').$type<Uint8Array>().notNull(),
    hash: blob('hash').$type<Uint8Array>().notNull(),
    isError: int('isError', {
      mode: 'boolean',
    }).notNull(),
    to: blob('to').$type<Uint8Array>().notNull(),
    value: blob('value').$type<Uint8Array>().notNull(),
  },
  (table) => [
    uniqueIndex('idx_evmInternalTransaction').on(
      table.chainId,
      table.contractAddress,
      table.from,
      table.gas,
      table.hash,
      table.isError,
      table.to,
      table.value,
    ),
  ],
)

export const actionTable = sqliteTable(
  'action',
  {
    id: text('id').primaryKey(),
    sourceId: text('sourceId')
      .notNull()
      .references(() => actionSourceTable.id, {
        onDelete: 'cascade',
      }),
    order: int('order').notNull(),
    type: text('type', { enum: actionTypes }).notNull(),
    subType: int('subType'),
    evidence: text('evidence', {
      enum: actionEvidences,
    }).notNull(),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
    totalPl: text('totalPl'),
    totalPlSortKey: text('totalPlSortKey'),
    comment: text('comment'),
    app: text('app'),
    loanId: text('loanId'),
    crossId: text('crossId'),
    crossType: text('crossType', {
      enum: actionCrossTypes,
    }),
    targetId: text('targetId').references(() => nftTable.id),
    locked: int('locked', { mode: 'boolean' }).notNull(),
    generatedTx: int('generatedTx', { mode: 'boolean' }).notNull(),
    relatedAssetIds: text('relatedAssetIds'),
    fromAddresses: text('fromAddresses'),
    toAddresses: text('toAddresses'),
    hasUnknownPriceTx: int('hasUnknownPriceTx', { mode: 'boolean' }),
    hasInsufficientAmountTx: int('hasInsufficientAmountTx', {
      mode: 'boolean',
    }),
    apiId: int('apiId').references(() => accountApiTable.id, {
      onDelete: 'cascade',
    }),
    csvId: int('csvId').references(() => accountCsvTable.id, {
      onDelete: 'cascade',
    }),
  },
  (table) => [
    index('idx_action').on(
      table.sourceId,
      table.order,
      table.type,
      table.subType,
      table.evidence,
      table.timestamp,
      table.app,
      table.targetId,
      table.locked,
      table.relatedAssetIds,
      table.fromAddresses,
      table.toAddresses,
      table.hasUnknownPriceTx,
      table.hasInsufficientAmountTx,
    ),
    index('idx_action_type').on(table.type),
    index('idx_action_subType').on(table.subType),
    index('idx_action_evidence').on(table.evidence),
    index('idx_action_timestamp').on(table.timestamp),
    index('idx_action_totalPlSortKey').on(table.totalPlSortKey),
    index('idx_action_app').on(table.app),
    index('targetIdIdx').on(table.targetId),
    index('idx_action_locked').on(table.locked),
    index('idx_action_relatedAssetIds').on(table.relatedAssetIds),
    index('idx_action_fromAddresses').on(table.fromAddresses),
    index('idx_action_toAddresses').on(table.toAddresses),
    index('idx_action_hasUnknownPriceTx').on(table.hasUnknownPriceTx),
    index('idx_action_hasInsufficientAmountTx').on(
      table.hasInsufficientAmountTx,
    ),
    index('idx_action_apiId').on(table.apiId),
    index('idx_action_csvId').on(table.csvId),
    uniqueIndex('unq_action_sourceId_order').on(table.sourceId, table.order),
  ],
)

export const actionsRelations = relations(actionTable, ({ one, many }) => ({
  actionTransferTable: many(actionTransferTable),
  actionSourceTable: one(actionSourceTable, {
    fields: [actionTable.sourceId],
    references: [actionSourceTable.id],
  }),
  nftTable: one(nftTable, {
    fields: [actionTable.targetId],
    references: [nftTable.id],
  }),
  transactionList: many(accountingTransactionTable),
  assistantProposalTable: many(assistantProposalTable),
  assistantProposalActionTable: many(assistantProposalActionTable),
}))

export const actionTransferTable = sqliteTable(
  'actionTransfer',
  {
    actionId: text('actionId')
      .notNull()
      .references(() => actionTable.id, {
        onDelete: 'cascade',
      }),
    order: int('order').notNull(),
    direction: text('direction', {
      enum: actionTransferDirections,
    }).notNull(),
    from: text('from'),
    to: text('to'),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    fiatId: text('fiatId'),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
    price: text('price'),
  },
  (table) => [
    uniqueIndex('idx_actionTransfer').on(table.actionId, table.order),
  ],
)

export const actionTransfersRelations = relations(
  actionTransferTable,
  ({ one }) => ({
    actionTable: one(actionTable, {
      fields: [actionTransferTable.actionId],
      references: [actionTable.id],
    }),
    cryptoCurrencyTable: one(cryptoCurrencyTable, {
      fields: [actionTransferTable.cryptoId],
      references: [cryptoCurrencyTable.id],
    }),
    nftTable: one(nftTable, {
      fields: [actionTransferTable.nftId],
      references: [nftTable.id],
    }),
  }),
)

export const actionSourceTable = sqliteTable(
  'actionSource',
  {
    id: text('id').primaryKey(),
    type: text('type', {
      enum: ['evm', 'polkadot', 'solana', 'cardano', 'xrpl', 'other'],
    }).notNull(),
    totalPl: text('totalPl'),
    totalPlSortKey: text('totalPlSortKey'),
    evmChainId: int('evmChainId'),
    evmHash: blob('evmHash').$type<Uint8Array>(),
    evmFrom: blob('evmFrom').$type<Uint8Array>(),
    evmTo: blob('evmTo').$type<Uint8Array>(),
    evmStatus: text('evmStatus', { enum: ['success', 'reverted'] }),
    evmFunctionId: blob('evmFunctionId').$type<Uint8Array>(),
    evmFunctionName: text('evmFunctionName'),
    polkadotChainId: int('polkadotChainId'),
    polkadotBlockNumber: int('polkadotBlockNumber'),
    polkadotExtrinsicIndex: int('polkadotExtrinsicIndex'),
    solanaSignature: text('solanaSignature'),
    /** XRPL の確定済み transaction hash。 */
    xrplHash: text('xrplHash'),
    solanaStatus: text('solanaStatus', {
      enum: ['success', 'reverted'],
    }),
    cardanoKind: text('cardanoKind', { enum: ['tx', 'reward'] }),
    cardanoTxHash: text('cardanoTxHash'),
    cardanoStakeAddress: text('cardanoStakeAddress'),
    cardanoPayoutEpoch: int('cardanoPayoutEpoch'),
    tokenTransferCount: int('tokenTransferCount'),
    tokenTransferCountVersion: int('tokenTransferCountVersion'),
  },
  (table) => [
    index('idx_actionSource_totalPlSortKey').on(table.totalPlSortKey),
  ],
)

export const actionSourceRelations = relations(
  actionSourceTable,
  ({ many }) => ({
    actionTable: many(actionTable),
  }),
)

/** ユーザー確認を待つ取引履歴アシスタント提案。 */
export const assistantProposalTable = sqliteTable(
  'assistantProposal',
  {
    id: text('id').primaryKey(),
    ruleKey: text('ruleKey').notNull(),
    ruleVersion: int('ruleVersion').notNull(),
    rulePriority: int('rulePriority').notNull(),
    primarySourceId: text('primarySourceId').notNull(),
    primaryActionOrder: int('primaryActionOrder').notNull(),
    primaryActionTimestamp: int('primaryActionTimestamp', {
      mode: 'timestamp_ms',
    }).notNull(),
    changePayloadJson: text('changePayloadJson').notNull(),
    explanationJson: text('explanationJson').notNull(),
    createdAt: int('createdAt', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [
    index('idx_assistantProposal_rule_cursor').on(
      table.ruleKey,
      table.primaryActionTimestamp,
      table.primarySourceId,
      table.primaryActionOrder,
      table.id,
    ),
    foreignKey({
      columns: [table.primarySourceId, table.primaryActionOrder],
      foreignColumns: [actionTable.sourceId, actionTable.order],
      name: 'fk_assistantProposal_primaryAction',
    }).onDelete('cascade'),
  ],
)

/** 提案と変更対象または判定参照Actionの関連。 */
export const assistantProposalActionTable = sqliteTable(
  'assistantProposalAction',
  {
    proposalId: text('proposalId')
      .notNull()
      .references(() => assistantProposalTable.id, {
        onDelete: 'cascade',
      }),
    sourceId: text('sourceId').notNull(),
    actionOrder: int('actionOrder').notNull(),
    role: text('role', { enum: ['target', 'reference'] }).notNull(),
    expectedHash: text('expectedHash').notNull(),
    displayOrder: int('displayOrder').notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.sourceId, table.actionOrder],
      foreignColumns: [actionTable.sourceId, actionTable.order],
      name: 'fk_assistantProposalAction_action',
    }),
    uniqueIndex('unq_assistantProposalAction').on(
      table.proposalId,
      table.sourceId,
      table.actionOrder,
      table.role,
    ),
    uniqueIndex('unq_assistantProposalAction_target')
      .on(table.sourceId, table.actionOrder)
      .where(sql`${table.role} = 'target'`),
  ],
)

/** 拒否済みの意味上同一な提案を再表示しないための識別情報。 */
export const assistantRejectedFingerprintTable = sqliteTable(
  'assistantRejectedFingerprint',
  {
    fingerprint: text('fingerprint').primaryKey(),
    ruleKey: text('ruleKey').notNull(),
    ruleVersion: int('ruleVersion').notNull(),
    revalidationPayloadJson: text('revalidationPayloadJson').notNull(),
    createdAt: int('createdAt', { mode: 'timestamp_ms' }).notNull(),
  },
)

/** 保留提案から主対象Actionと全関連Actionを参照するrelation。 */
export const assistantProposalRelations = relations(
  assistantProposalTable,
  ({ one, many }) => ({
    primaryAction: one(actionTable, {
      fields: [
        assistantProposalTable.primarySourceId,
        assistantProposalTable.primaryActionOrder,
      ],
      references: [actionTable.sourceId, actionTable.order],
    }),
    actions: many(assistantProposalActionTable),
  }),
)

/** 提案Action関連から提案本体とActionを参照するrelation。 */
export const assistantProposalActionRelations = relations(
  assistantProposalActionTable,
  ({ one }) => ({
    proposal: one(assistantProposalTable, {
      fields: [assistantProposalActionTable.proposalId],
      references: [assistantProposalTable.id],
    }),
    action: one(actionTable, {
      fields: [
        assistantProposalActionTable.sourceId,
        assistantProposalActionTable.actionOrder,
      ],
      references: [actionTable.sourceId, actionTable.order],
    }),
  }),
)

export const customActionSubTypeTable = sqliteTable(
  'customActionSubType',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    type: text('type', { enum: actionTypes }).notNull(),
    name: text('name').notNull(),
  },
  (table) => [index('idx_customActionSubType_type').on(table.type)],
)

export const actionAccountRelationTable = sqliteTable(
  'actionAccountRelation',
  {
    actionId: text('actionId')
      .notNull()
      .references(() => actionTable.id, {
        onDelete: 'cascade',
      }),
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
  },
  (table) => [
    uniqueIndex('idx_actionAccountRelation').on(
      table.actionId,
      table.accountId,
    ),
    index('idx_actionAccountRelation_accountId').on(table.accountId),
  ],
)

export const lockedPeriodActionTable = sqliteTable(
  'lockedPeriodAction',
  {
    id: text('id').primaryKey(),
    sourceId: text('sourceId').notNull(),
    order: int('order').notNull(),
    type: text('type', { enum: actionTypes }).notNull(),
    subType: int('subType'),
    evidence: text('evidence', {
      enum: actionEvidences,
    }).notNull(),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
    comment: text('comment'),
    app: text('app'),
    loanId: text('loanId'),
    crossId: text('crossId'),
    crossType: text('crossType', {
      enum: actionCrossTypes,
    }),
    targetId: text('targetId').references(() => nftTable.id),
    locked: int('locked', { mode: 'boolean' }).notNull(),
    generatedTx: int('generatedTx', { mode: 'boolean' }).notNull(),
    relatedAssetIds: text('relatedAssetIds'),
    fromAddresses: text('fromAddresses'),
    toAddresses: text('toAddresses'),
    hasUnknownPriceTx: int('hasUnknownPriceTx', { mode: 'boolean' }),
    hasInsufficientAmountTx: int('hasInsufficientAmountTx', {
      mode: 'boolean',
    }),
    csvId: int('csvId').references(() => accountCsvTable.id, {
      onDelete: 'cascade',
    }),
    apiId: int('apiId').references(() => accountApiTable.id, {
      onDelete: 'cascade',
    }),
  },
  (table) => [
    uniqueIndex('idx_lockedPeriodAction_sourceId_order').on(
      table.sourceId,
      table.order,
    ),
    index('idx_lockedPeriodAction_timestamp').on(table.timestamp),
    index('idx_lockedPeriodAction_csvId').on(table.csvId),
    index('idx_lockedPeriodAction_apiId').on(table.apiId),
  ],
)

export const lockedPeriodActionTransferTable = sqliteTable(
  'lockedPeriodActionTransfer',
  {
    actionId: text('actionId')
      .notNull()
      .references(() => lockedPeriodActionTable.id, {
        onDelete: 'cascade',
      }),
    order: int('order').notNull(),
    direction: text('direction', {
      enum: actionTransferDirections,
    }).notNull(),
    from: text('from'),
    to: text('to'),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    fiatId: text('fiatId'),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
    price: text('price'),
  },
  (table) => [
    uniqueIndex('idx_lockedPeriodActionTransfer').on(
      table.actionId,
      table.order,
    ),
  ],
)

export const lockedPeriodActionAccountRelationTable = sqliteTable(
  'lockedPeriodActionAccountRelation',
  {
    actionId: text('actionId')
      .notNull()
      .references(() => lockedPeriodActionTable.id, {
        onDelete: 'cascade',
      }),
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
  },
  (table) => [
    uniqueIndex('idx_lockedPeriodActionAccountRelation').on(
      table.actionId,
      table.accountId,
    ),
    index('idx_lockedPeriodActionAccountRelation_accountId').on(
      table.accountId,
    ),
  ],
)

export const cryptoCurrencyTable = sqliteTable('cryptoCurrency', {
  id: text('id').primaryKey(),
  name: text('name').notNull(),
  symbol: text('symbol').notNull(),
  icon: text('icon'),
  coingeckoId: text('coingeckoId'),
  marketCapUsd: integer('marketCapUsd'),
  priceDatasourceId: text('priceDatasourceId'),
  updatedAt: integer('updatedAt').notNull(),
})

export const nftTable = sqliteTable('nft', {
  id: text('id').primaryKey(),
  name: text('name'),
  image: text('image'),
  metadata: text('metadata'),
  updatedAt: integer('updatedAt').notNull(),
})

/** @deprecated Do not use this table. This is only for backward compatibility. */
export const erc20TokenTable = sqliteTable(
  'erc20Token',
  {
    id: text('id').primaryKey(),
    chainId: int('chainId').notNull(),
    address: blob('address').$type<Uint8Array>(),
    name: text('name').notNull(),
    symbol: text('symbol').notNull(),
    decimals: int('decimals').notNull(),
    currencyId: text('currencyId').references(() => cryptoCurrencyTable.id),
  },
  (table) => [uniqueIndex('idx_erc20Token').on(table.chainId, table.address)],
)

/** @deprecated Do not use this table. This is only for backward compatibility. */
export const accountErc20TokenRelationTable = sqliteTable(
  'accountErc20TokenRelation',
  {
    accountId: int('accountId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    tokenId: text('tokenId')
      .notNull()
      .references(() => erc20TokenTable.id),
  },
  (table) => [
    uniqueIndex('idx_accountErc20TokenRelation').on(
      table.accountId,
      table.tokenId,
    ),
  ],
)

export const singleActionRuleTable = sqliteTable(
  'singleActionRule',
  {
    source: text('source').notNull(),
    targetType: text('targetType', { enum: actionTypes }).notNull(),
    newType: text('newType', { enum: actionTypes }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_singleActionRule').on(table.source, table.targetType),
  ],
)

/** ユーザー個別のカスタムルール */
export const actionEditRuleTable = sqliteTable('actionEditRule', {
  id: int('id').primaryKey({ autoIncrement: true }),
  /** 条件: app の比較演算子 */
  conditionAppOp: text('conditionAppOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: app の比較値 */
  conditionAppValue: blob('conditionAppValue', { mode: 'json' }),
  /** 条件: transfer asset の比較演算子 */
  conditionAssetOp: text('conditionAssetOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: transfer asset の比較値 */
  conditionAssetValue: blob('conditionAssetValue', { mode: 'json' }),
  /** 条件: category の比較演算子 */
  conditionCategoryOp: text('conditionCategoryOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: category の比較値 */
  conditionCategoryValue: blob('conditionCategoryValue', { mode: 'json' }),
  /** 条件: comment の比較演算子 */
  conditionCommentOp: text('conditionCommentOp', {
    enum: textConditionOperators,
  }),
  /** 条件: comment の比較値 */
  conditionCommentValue: text('conditionCommentValue'),
  /** 条件: cross id の比較演算子 */
  conditionCrossIdOp: text('conditionCrossIdOp', {
    enum: textConditionOperators,
  }),
  /** 条件: cross id の比較値 */
  conditionCrossIdValue: text('conditionCrossIdValue'),
  /** 条件: cross type の比較演算子 */
  conditionCrossTypeOp: text('conditionCrossTypeOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: cross type の比較値 */
  conditionCrossTypeValue: blob('conditionCrossTypeValue', { mode: 'json' }),
  /** 条件: evidence の比較演算子 */
  conditionEvidenceOp: text('conditionEvidenceOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: evidence の比較値 */
  conditionEvidenceValue: blob('conditionEvidenceValue', { mode: 'json' }),
  /** 条件: evm function の比較演算子 */
  conditionEvmFunctionOp: text('conditionEvmFunctionOp', {
    enum: textConditionOperators,
  }),
  /** 条件: evm function の比較値 */
  conditionEvmFunctionValue: text('conditionEvmFunctionValue'),
  /** 条件: evm to (address) の比較演算子 */
  conditionEvmToOp: text('conditionEvmToOp', {
    enum: textConditionOperators,
  }),
  /** 条件: evm to (address) の比較値 */
  conditionEvmToValue: text('conditionEvmToValue'),
  /** 条件: loan id の比較演算子 */
  conditionLoanIdOp: text('conditionLoanIdOp', {
    enum: textConditionOperators,
  }),
  /** 条件: loan id の比較値 */
  conditionLoanIdValue: text('conditionLoanIdValue'),
  /** 条件: transfer from/to の比較演算子 */
  conditionTransferOp: text('conditionTransferOp', {
    enum: transferConditionOperators,
  }),
  /** 条件: transfer from/to の比較値 */
  conditionTransferValue: blob('conditionTransferValue', { mode: 'json' }),
  /** 条件: amount の比較演算子 */
  conditionAmountOp: text('conditionAmountOp', {
    enum: mathConditionOperators,
  }),
  /** 条件: amount の比較値 */
  conditionAmountValue: text('conditionAmountValue'),
  /** 条件: direction=in transfer の数の比較演算子 */
  conditionTransferInOp: text('conditionTransferInOp', {
    enum: mathConditionOperators,
  }),
  /** 条件: direction=in transfer の数の比較値 */
  conditionTransferInValue: int('conditionTransferInValue'),
  /** 条件: direction=out transfer の数の比較演算子 */
  conditionTransferOutOp: text('conditionTransferOutOp', {
    enum: mathConditionOperators,
  }),
  /** 条件: direction=out transfer の数の比較値 */
  conditionTransferOutValue: int('conditionTransferOutValue'),
  /** 条件: type の比較演算子 */
  conditionTypeOp: text('conditionTypeOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: type の比較値 */
  conditionTypeValue: blob('conditionTypeValue', { mode: 'json' }),
  /** 条件: sub type の比較演算子 */
  conditionSubTypeOp: text('conditionSubTypeOp', {
    enum: arrayConditionOperators,
  }),
  /** 条件: sub type の比較値 */
  conditionSubTypeValue: blob('conditionSubTypeValue', { mode: 'json' }),
  /** 適用処理: amount の値 */
  editAmountValue: text('editAmountValue'),
  /** 適用処理: amount の対象とする transfer asset */
  editAmountTargetAsset: text('editAmountTargetAsset'),
  /** 適用処理: amount の対象とする transfer direction */
  editAmountTargetDirection: text('editAmountTargetDirection', {
    enum: actionTransferDirections,
  }),
  /** 適用処理: amount の対象とする transfer price */
  editAmountTargetPrice: text('editAmountTargetPrice', {
    enum: priceTargetOperators,
  }),
  /** 適用処理: asset の値 */
  editAssetValue: text('editAssetValue'),
  /** 適用処理: asset の対象とする transfer asset */
  editAssetTargetAsset: text('editAssetTargetAsset'),
  /** 適用処理: asset の対象とする transfer direction */
  editAssetTargetDirection: text('editAssetTargetDirection', {
    enum: actionTransferDirections,
  }),
  /** 適用処理: asset の対象とする transfer price */
  editAssetTargetPrice: text('editAssetTargetPrice', {
    enum: priceTargetOperators,
  }),
  /** 適用処理: comment の値 */
  editCommentValue: text('editCommentValue'),
  /** 適用処理: cross id の値 */
  editCrossIdValue: text('editCrossIdValue'),
  /** 適用処理: cross type の値 */
  editCrossTypeValue: text('editCrossTypeValue', {
    enum: actionCrossTypes,
  }),
  /** 適用処理: evidence の値 */
  editEvidenceValue: text('editEvidenceValue', {
    enum: actionEvidences,
  }),
  /** 適用処理: loan id の値 */
  editLoanIdValue: text('editLoanIdValue'),
  /** 適用処理: locked の値 */
  editLockedValue: int('editLockedValue', {
    mode: 'boolean',
  }),
  /** 適用処理: price の値 */
  editPriceValue: text('editPriceValue'),
  /** 適用処理: price の対象とする transfer asset */
  editPriceTargetAsset: text('editPriceTargetAsset'),
  /** 適用処理: price の対象とする transfer direction */
  editPriceTargetDirection: text('editPriceTargetDirection', {
    enum: actionTransferDirections,
  }),
  /** 適用処理: price の対象とする transfer price */
  editPriceTargetPrice: text('editPriceTargetPrice', {
    enum: priceTargetOperators,
  }),
  /** 適用処理: type の値 */
  editTypeValue: text('editTypeValue', {
    enum: [...actionTypes, 'convert-to-jpy-trade'],
  }),
  /** 適用処理: sub type の値 */
  editSubTypeValue: int('editSubTypeValue'),
  /** 停止中フラグ */
  stopped: int('stopped', {
    mode: 'boolean',
  }),
  /** ルール名 */
  name: text('name').notNull(),
  /** ルール適用の優先順位 */
  priority: int('priority').notNull(),
})

/** コメント候補テンプレート */
export const commentCandidateTable = sqliteTable('commentCandidate', {
  id: int('id').primaryKey({ autoIncrement: true }),
  /** 表示順 */
  displayOrder: int('displayOrder').notNull(),
  /** コメント候補を構成するパーツ配列 */
  parts: text('parts', { mode: 'json' }).notNull(),
})

/** 取引履歴フィルター設定 */
export const historyFilterPresetTable = sqliteTable('historyFilterPreset', {
  id: int('id').primaryKey({ autoIncrement: true }),
  /** 表示順 */
  displayOrder: int('displayOrder').notNull(),
  /** フィルター設定名 */
  name: text('name').notNull(),
  /** URL query 由来の hf_* フィルター設定 */
  filters: text('filters', { mode: 'json' }).notNull(),
})

export const accountingPeriodTable = sqliteTable('accountingPeriod', {
  id: int('id').primaryKey(),
  start: int('start', { mode: 'timestamp_ms' }).notNull(),
  end: int('end', { mode: 'timestamp_ms' }).notNull(),
})

export const assetBalanceHistoryTable = sqliteTable(
  'assetBalanceHistory',
  {
    accountId: int('accountingId')
      .notNull()
      .references(() => accountTable.id, {
        onDelete: 'cascade',
      }),
    key: text('key').notNull(),
    day: int('day').notNull(),
    hour: int('hour').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    fiatId: text('fiatId'),
    amount: text('amount').notNull(),
    price: text('price'),
    timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_assetBalanceHistory').on(
      table.accountId,
      table.key,
      table.day,
      table.hour,
      table.cryptoId,
      table.fiatId,
    ),
  ],
)

export const assetBalanceTable = sqliteTable(
  'assetBalance',
  {
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    asset: text('asset').notNull(),
    order: int('order').notNull(),
    value: text('value').notNull(),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_assetBalance').on(
      table.accountingPeriodId,
      table.asset,
      table.order,
    ),
  ],
)

export const assetBorrowTable = sqliteTable(
  'assetBorrow',
  {
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    asset: text('asset').notNull(),
    order: int('order').notNull(),
    value: text('value').notNull(),
    amount: text('amount').notNull(),
    borrowedValue: text('borrowedValue').notNull(),
    borrowedAmount: text('borrowedAmount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_assetBorrow').on(
      table.accountingPeriodId,
      table.asset,
      table.order,
    ),
  ],
)

export const nftAcquiredTimestampTable = sqliteTable(
  'nftAcquiredTimestamp',
  {
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    nftId: text('nftId')
      .notNull()
      .references(() => nftTable.id, {
        onDelete: 'cascade',
      }),
    timestamp: int('timestamp', {
      mode: 'timestamp_ms',
    }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_nftAcquiredTimestamp').on(
      table.accountingPeriodId,
      table.nftId,
    ),
  ],
)

export const loanTable = sqliteTable(
  'loan',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    loanId: text('loanId').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loan').on(table.accountingPeriodId, table.loanId),
    index('idx_loan_accountingPeriodId').on(table.accountingPeriodId),
  ],
)

export const loanRelations = relations(loanTable, ({ many }) => ({
  loanBorrows: many(loanBorrowTable),
  loanBorrowWithDebts: many(loanBorrowWithDebtTable),
  loanBorrowWithDebtPositions: many(loanBorrowWithDebtPositionTable),
  loanDeposits: many(loanDepositTable),
  loanDepositWithBonds: many(loanDepositWithBondTable),
  loanDepositWithBondPositions: many(loanDepositWithBondPositionTable),
}))

export const loanBorrowTable = sqliteTable(
  'loanBorrow',
  {
    loanId: int('loanId')
      .notNull()
      .references(() => loanTable.id, {
        onDelete: 'cascade',
      }),
    key: text('key').notNull(),
    order: int('order').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loanBorrowPosition').on(
      table.loanId,
      table.key,
      table.order,
    ),
  ],
)

export const loanBorrowRelations = relations(loanBorrowTable, ({ one }) => ({
  loan: one(loanTable, {
    fields: [loanBorrowTable.loanId],
    references: [loanTable.id],
  }),
  crypto: one(cryptoCurrencyTable, {
    fields: [loanBorrowTable.cryptoId],
    references: [cryptoCurrencyTable.id],
  }),
  nft: one(nftTable, {
    fields: [loanBorrowTable.nftId],
    references: [nftTable.id],
  }),
}))

export const loanBorrowWithDebtTable = sqliteTable(
  'loanBorrowWithDebt',
  {
    loanId: int('loanId')
      .notNull()
      .references(() => loanTable.id, {
        onDelete: 'cascade',
      }),
    key: text('key').notNull(),
    order: int('order').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loanBorrowWithDebt').on(
      table.loanId,
      table.key,
      table.order,
    ),
  ],
)

export const loanBorrowWithDebtRelations = relations(
  loanBorrowWithDebtTable,
  ({ one }) => ({
    loan: one(loanTable, {
      fields: [loanBorrowWithDebtTable.loanId],
      references: [loanTable.id],
    }),
    crypto: one(cryptoCurrencyTable, {
      fields: [loanBorrowWithDebtTable.cryptoId],
      references: [cryptoCurrencyTable.id],
    }),
    nft: one(nftTable, {
      fields: [loanBorrowWithDebtTable.nftId],
      references: [nftTable.id],
    }),
  }),
)

export const loanBorrowWithDebtPositionTable = sqliteTable(
  'loanBorrowWithDebtPosition',
  {
    loanId: int('loanId')
      .notNull()
      .references(() => loanTable.id, {
        onDelete: 'cascade',
      }),
    debtKey: text('debtKey').notNull(),
    debtOrder: int('debtOrder').notNull(),
    positionOrder: int('positionOrder').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loanBorrowWithDebtPosition').on(
      table.loanId,
      table.debtKey,
      table.debtOrder,
      table.positionOrder,
    ),
  ],
)

export const loanBorrowWithDebtPositionRelations = relations(
  loanBorrowWithDebtPositionTable,
  ({ one }) => ({
    loan: one(loanTable, {
      fields: [loanBorrowWithDebtPositionTable.loanId],
      references: [loanTable.id],
    }),
    crypto: one(cryptoCurrencyTable, {
      fields: [loanBorrowWithDebtPositionTable.cryptoId],
      references: [cryptoCurrencyTable.id],
    }),
    nft: one(nftTable, {
      fields: [loanBorrowWithDebtPositionTable.nftId],
      references: [nftTable.id],
    }),
  }),
)

export const loanDepositTable = sqliteTable(
  'loanDeposit',
  {
    loanId: int('loanId')
      .notNull()
      .references(() => loanTable.id, {
        onDelete: 'cascade',
      }),
    key: text('key').notNull(),
    order: int('order').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loanDeposit').on(table.loanId, table.key, table.order),
  ],
)

export const loanDepositRelations = relations(loanDepositTable, ({ one }) => ({
  loan: one(loanTable, {
    fields: [loanDepositTable.loanId],
    references: [loanTable.id],
  }),
  crypto: one(cryptoCurrencyTable, {
    fields: [loanDepositTable.cryptoId],
    references: [cryptoCurrencyTable.id],
  }),
  nft: one(nftTable, {
    fields: [loanDepositTable.nftId],
    references: [nftTable.id],
  }),
}))

export const loanDepositWithBondTable = sqliteTable(
  'loanDepositWithBond',
  {
    loanId: int('loanId')
      .notNull()
      .references(() => loanTable.id, {
        onDelete: 'cascade',
      }),
    key: text('key').notNull(),
    order: int('order').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loanDepositWithBond').on(
      table.loanId,
      table.key,
      table.order,
    ),
  ],
)

export const loanDepositWithBondRelations = relations(
  loanDepositWithBondTable,
  ({ one }) => ({
    loan: one(loanTable, {
      fields: [loanDepositWithBondTable.loanId],
      references: [loanTable.id],
    }),
    crypto: one(cryptoCurrencyTable, {
      fields: [loanDepositWithBondTable.cryptoId],
      references: [cryptoCurrencyTable.id],
    }),
    nft: one(nftTable, {
      fields: [loanDepositWithBondTable.nftId],
      references: [nftTable.id],
    }),
  }),
)

export const loanDepositWithBondPositionTable = sqliteTable(
  'loanDepositWithBondPosition',
  {
    loanId: int('loanId')
      .notNull()
      .references(() => loanTable.id, {
        onDelete: 'cascade',
      }),
    bondKey: text('bondKey').notNull(),
    bondOrder: int('bondOrder').notNull(),
    positionOrder: int('positionOrder').notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
  },
  (table) => [
    uniqueIndex('idx_loanDepositWithBondPosition').on(
      table.loanId,
      table.bondKey,
      table.bondOrder,
      table.positionOrder,
    ),
  ],
)

export const loanDepositWithBondPositionRelations = relations(
  loanDepositWithBondPositionTable,
  ({ one }) => ({
    loan: one(loanTable, {
      fields: [loanDepositWithBondPositionTable.loanId],
      references: [loanTable.id],
    }),
    crypto: one(cryptoCurrencyTable, {
      fields: [loanDepositWithBondPositionTable.cryptoId],
      references: [cryptoCurrencyTable.id],
    }),
    nft: one(nftTable, {
      fields: [loanDepositWithBondPositionTable.nftId],
      references: [nftTable.id],
    }),
  }),
)

export const crossActionBundleTable = sqliteTable(
  'crossActionBundle',
  {
    id: int('id').primaryKey({ autoIncrement: true }),
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    crossActionBundleId: text('crossActionBundleId').notNull(),
  },
  (table) => [
    uniqueIndex('idx_crossActionBundle').on(
      table.accountingPeriodId,
      table.crossActionBundleId,
    ),
  ],
)

export const crossActionBundleRelations = relations(
  crossActionBundleTable,
  ({ many }) => ({
    crossActionBundleRelationList: many(crossActionBundleRelationTable),
  }),
)

export const crossActionBundleRelationTable = sqliteTable(
  'crossActionBundleRelation',
  {
    crossActionBundleId: int('crossActionBundleId')
      .notNull()
      .references(() => crossActionBundleTable.id, {
        onDelete: 'cascade',
      }),
    actionId: text('actionId')
      .notNull()
      .references(() => actionTable.id, {
        onDelete: 'cascade',
      }),
  },
  (table) => [
    uniqueIndex('idx_crossActionBundleRelation').on(
      table.crossActionBundleId,
      table.actionId,
    ),
    index('idx_crossActionBundleRelation_actionId').on(table.actionId),
  ],
)

export const crossActionBundleRelationRelations = relations(
  crossActionBundleRelationTable,
  ({ one }) => ({
    crossActionBundle: one(crossActionBundleTable, {
      fields: [crossActionBundleRelationTable.crossActionBundleId],
      references: [crossActionBundleTable.id],
    }),
    action: one(actionTable, {
      fields: [crossActionBundleRelationTable.actionId],
      references: [actionTable.id],
    }),
  }),
)

export const accountingTransactionTable = sqliteTable(
  'accountingTransaction',
  {
    id: text('id').primaryKey(),
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    actionId: text('actionId')
      .notNull()
      .references(() => actionTable.id, {
        onDelete: 'cascade',
      }),
    order: int('order').notNull(),
    category: text('category', {
      enum: transactionCategories,
    }).notNull(),
    subType: int('subType'),
    subCategory: text('subCategory', {
      enum: transactionSubCategories,
    }),
    targetId: text('targetId').references(() => nftTable.id),
    timestamp: int('timestamp', {
      mode: 'timestamp_ms',
    }).notNull(),
    comment: text('comment'),
    updatedAt: int('updatedAt', {
      mode: 'timestamp_ms',
    }).notNull(),
  },
  (table) => [
    uniqueIndex('idx_accountingTransaction').on(table.actionId, table.order),
    index('idx_accountingTransaction_accountingPeriodId').on(
      table.accountingPeriodId,
    ),
    index('idx_accountingTransaction_subType').on(table.subType),
    index('idx_accountingTransaction_timestamp_id').on(
      table.timestamp,
      table.id,
    ),
  ],
)

export const accountingTransactionRelations = relations(
  accountingTransactionTable,
  ({ one, many }) => ({
    action: one(actionTable, {
      fields: [accountingTransactionTable.actionId],
      references: [actionTable.id],
    }),
    transferList: many(accountingTransactionTransferTable),
    nft: one(nftTable, {
      fields: [accountingTransactionTable.targetId],
      references: [nftTable.id],
    }),
  }),
)

export const accountingTransactionTransferTable = sqliteTable(
  'accountingTransactionTransfer',
  {
    transactionId: text('transactionId')
      .notNull()
      .references(() => accountingTransactionTable.id, {
        onDelete: 'cascade',
      }),
    order: int('order').notNull(),
    isIn: int('isIn', {
      mode: 'boolean',
    }).notNull(),
    cryptoId: text('cryptoId').references(() => cryptoCurrencyTable.id),
    fiatId: text('fiatId'),
    nftId: text('nftId').references(() => nftTable.id),
    amount: text('amount').notNull(),
    price: text('price').notNull(),
    zeroCompleted: int('zeroCompleted', {
      mode: 'boolean',
    }).notNull(),
    cost: text('cost').notNull(),
    balance: text('balance').notNull(),
    borrowing: text('borrowing').notNull(),
    totalPl: text('totalPl').notNull(),
    tradePl: text('tradePl').notNull(),
    separateTradePl: text('separateTradePl').default('0').notNull(),
    nftTradePl: text('nftTradePl').default('0').notNull(),
    nftCapitalPl: text('nftCapitalPl').default('0').notNull(),
    incomePl: text('incomePl').notNull(),
    separateRealizedPl: text('separateRealizedPl').default('0').notNull(),
    realizedPl: text('realizedPl').default('0').notNull(),
    airdropPl: text('airdropPl').default('0').notNull(),
    feePl: text('feePl').notNull(),
  },
  (table) => [
    uniqueIndex('idx_accountingTransactionTransfer').on(
      table.transactionId,
      table.order,
    ),
  ],
)

export const accountingTransactionTransferRelations = relations(
  accountingTransactionTransferTable,
  ({ one }) => ({
    transaction: one(accountingTransactionTable, {
      fields: [accountingTransactionTransferTable.transactionId],
      references: [accountingTransactionTable.id],
    }),
    cryptoCurrency: one(cryptoCurrencyTable, {
      fields: [accountingTransactionTransferTable.cryptoId],
      references: [cryptoCurrencyTable.id],
    }),
    nft: one(nftTable, {
      fields: [accountingTransactionTransferTable.nftId],
      references: [nftTable.id],
    }),
  }),
)

export const assetValueTable = sqliteTable(
  'assetValue',
  {
    accountingPeriodId: int('accountingPeriodId')
      .notNull()
      .references(() => accountingPeriodTable.id, {
        onDelete: 'cascade',
      }),
    asset: text('asset').notNull(),
    type: text('type', { enum: ['own', 'borrow', 'lend'] }).notNull(),
    amount: text('amount').notNull(),
    cost: text('cost').notNull(),
    value: text('value').notNull(),
    isKnownPeriodEndPrice: int('isKnownPeriodEndPrice', {
      mode: 'boolean',
    }).notNull(),
  },
  (table) => [unique().on(table.accountingPeriodId, table.asset, table.type)],
)

export const journalAccountTable = sqliteTable('journalAccount', {
  key: text('key').primaryKey(),
  account: text('account').notNull(),
  subAccount: text('subAccount').notNull(),
  debitTaxType: text('debitTaxType', {
    enum: taxTypes,
  }).notNull(),
  creditTaxType: text('creditTaxType', {
    enum: taxTypes,
  }).notNull(),
})

export const customJournalAccountTable = sqliteTable('customJournalAccount', {
  subTypeId: int('subTypeId')
    .primaryKey()
    .references(() => customActionSubTypeTable.id, {
      onDelete: 'cascade',
    }),
  account: text('account').notNull(),
  subAccount: text('subAccount').notNull(),
  debitTaxType: text('debitTaxType', {
    enum: taxTypes,
  }).notNull(),
  creditTaxType: text('creditTaxType', {
    enum: taxTypes,
  }).notNull(),
})

export const syncTable = sqliteTable('sync', {
  key: text('key').primaryKey(),
  timestamp: int('timestamp', { mode: 'timestamp_ms' }).notNull(),
})

/** 期間別の損益合計。集計中は totals を未設定にし generation で競合を検出する。 */
export const periodPlSummaryTable = sqliteTable('periodPlSummary', {
  accountingPeriodId: int('accountingPeriodId')
    .primaryKey()
    .references(() => accountingPeriodTable.id, { onDelete: 'cascade' }),
  start: int('start', { mode: 'timestamp_ms' }).notNull(),
  end: int('end', { mode: 'timestamp_ms' }).notNull(),
  generation: text('generation').notNull(),
  totals: text('totals'),
})
